import {
    faBirthdayCake,
    faBuilding,
    faCalendarAlt,
    faCalendarCheck,
    faEnvelope,
    faExclamationTriangle,
    faGenderless,
    faMapMarkerAlt,
    faMars,
    faPhone,
    faVenus
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge } from '@ui';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { formatClubMemberPropertyValue } from '../../helpers';
import { ClubMember, ClubMemberPropertyType } from '../../types';

interface CustomProp {
    label: string;
    type: ClubMemberPropertyType;
    value: any;
}

interface Props {
    clubMember: ClubMember;
    properties?: CustomProp[];
    remarks?: string | null;
}

// Single contact/personal item with icon, label and value stacked vertically
const ContactItem = ({
    icon,
    label,
    children
}: {
    icon: React.ReactNode;
    label: string;
    children: React.ReactNode;
}) => (
    <div className="tw-flex tw-items-start tw-gap-3">
        <span className="tw-mt-0.5 tw-w-4 tw-shrink-0 tw-text-accents-5">{icon}</span>
        <div className="tw-min-w-0 tw-flex-1">
            <p className="tw-mb-0.5 tw-text-xs tw-text-accents-6">{label}</p>
            <div className="tw-text-sm tw-font-medium tw-text-accents-9">{children}</div>
        </div>
    </div>
);

// Section header for additional properties
const SectionHeader = ({ title }: { title: string }) => (
    <p className="tw-mb-1 tw-mt-4 tw-text-sm tw-font-semibold tw-tracking-wider tw-text-accents-8 first:tw-mt-0">
        {title}
    </p>
);

// Single labelled row used for custom properties
const Row = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <div className="tw-flex tw-items-start tw-gap-4 tw-border-0 tw-border-b tw-border-solid tw-border-accents-2 tw-py-2 last:tw-border-b-0">
        <span className="tw-w-36 tw-shrink-0 tw-text-sm tw-text-accents-6">{label}</span>
        <span className="tw-min-w-0 tw-flex-1 tw-text-sm tw-font-medium tw-text-accents-9">{value}</span>
    </div>
);

const genderIcon = (gender: ClubMember['gender']) => {
    switch (gender) {
        case 'männlich':
            return <FontAwesomeIcon icon={faMars} />;
        case 'weiblich':
            return <FontAwesomeIcon icon={faVenus} />;
        case 'divers':
            return <FontAwesomeIcon icon={faGenderless} />;
        default:
            return null;
    }
};

const ClubMemberPropertiesTab = ({ clubMember, properties, remarks }: Props) => {
    const { t } = useTranslation('member-management');
    const address = clubMember?.address ?? {};
    const birthday = clubMember?.birthday ? moment(clubMember.birthday) : null;
    const addressComplete = !!(address.street && address.streetnumber && address.city && address.postalcode);

    return (
        <div className="tw-flex tw-flex-col tw-gap-6">
            {/* 2-column contact / personal grid */}
            <div className="tw-grid tw-grid-cols-1 tw-gap-6 sm:tw-grid-cols-2">
                {/* Left — Kontaktdaten */}
                <div className="tw-flex tw-flex-col tw-gap-4">
                    <h6 className="tw-mb-0 tw-text-sm tw-font-semibold tw-text-accents-8">
                        {t('clubMember.preview.properties.contactData')}
                    </h6>
                    <div className="tw-flex tw-flex-col tw-gap-3">
                        {clubMember.email && (
                            <ContactItem
                                icon={<FontAwesomeIcon icon={faEnvelope} />}
                                label={t('clubMember.preview.properties.email')}
                            >
                                <a
                                    href={`mailto:${clubMember.email}`}
                                    className="tw-text-success hover:tw-text-success/80 hover:tw-underline"
                                >
                                    {clubMember.email}
                                </a>
                            </ContactItem>
                        )}
                        {clubMember.phone && (
                            <ContactItem
                                icon={<FontAwesomeIcon icon={faPhone} />}
                                label={t('clubMember.preview.properties.phone')}
                            >
                                <a href={`tel:${clubMember.phone}`} className="tw-text-success hover:tw-text-success/80 hover:tw-underline">
                                    {clubMember.phone}
                                </a>
                            </ContactItem>
                        )}
                        <ContactItem
                            icon={
                                <span className="tw-flex tw-items-center tw-gap-1">
                                    <FontAwesomeIcon icon={faMapMarkerAlt} />
                                    {!addressComplete && (
                                        <FontAwesomeIcon
                                            icon={faExclamationTriangle}
                                            className="tw-text-[9px] tw-text-warning"
                                        />
                                    )}
                                </span>
                            }
                            label={t('clubMember.preview.properties.address')}
                        >
                            {addressComplete ? (
                                <span>
                                    {address.street} {address.streetnumber}
                                    <br />
                                    {address.postalcode} {address.city}
                                </span>
                            ) : (
                                <span className="tw-text-accents-4">
                                    {t('clubMember.preview.properties.addressIncomplete')}
                                </span>
                            )}
                        </ContactItem>
                    </div>
                </div>

                {/* Right — Persönliche Daten */}
                <div className="tw-flex tw-flex-col tw-gap-4">
                    <h6 className="tw-mb-0 tw-text-sm tw-font-semibold tw-text-accents-8">
                        {t('clubMember.preview.properties.personalData')}
                    </h6>
                    <div className="tw-flex tw-flex-col tw-gap-3">
                        {birthday && (
                            <ContactItem
                                icon={<FontAwesomeIcon icon={faBirthdayCake} />}
                                label={t('clubMember.preview.properties.birthday')}
                            >
                                {t('clubMember.preview.properties.birthdayWithAge', {
                                    date: birthday.format('ll'),
                                    count: moment().diff(birthday, 'years')
                                })}
                            </ContactItem>
                        )}
                        {clubMember.gender && (
                            <ContactItem
                                icon={genderIcon(clubMember.gender)}
                                label={t('clubMember.preview.properties.gender')}
                            >
                                {clubMember.gender}
                            </ContactItem>
                        )}
                        {clubMember.departments?.length > 0 && (
                            <ContactItem
                                icon={<FontAwesomeIcon icon={faBuilding} />}
                                label={t('clubMember.preview.properties.departments')}
                            >
                                {clubMember.departments.map((d) => d.title).join(', ')}
                            </ContactItem>
                        )}
                        {clubMember.entry_date && (
                            <ContactItem
                                icon={<FontAwesomeIcon icon={faCalendarAlt} />}
                                label={t('clubMember.preview.properties.entryDate')}
                            >
                                {moment(clubMember.entry_date).format('l')}
                            </ContactItem>
                        )}
                        {clubMember.exit_date && (
                            <ContactItem
                                icon={<FontAwesomeIcon icon={faCalendarCheck} />}
                                label={t('clubMember.preview.properties.exitDate')}
                            >
                                <span className="tw-text-warning">{moment(clubMember.exit_date).format('l')}</span>
                            </ContactItem>
                        )}
                        {clubMember.cancellation_received_on && (
                            <ContactItem
                                icon={<FontAwesomeIcon icon={faCalendarCheck} />}
                                label={t('clubMember.exit.properties.receivedOn')}
                            >
                                {moment(clubMember.cancellation_received_on).format('l')}
                                {clubMember.cancelled_by &&
                                    ` (${t('clubMember.exit.properties.cancelledBy')}: ${clubMember.cancelled_by})`}
                            </ContactItem>
                        )}
                        {clubMember.cancellation_reason && (
                            <ContactItem
                                icon={<FontAwesomeIcon icon={faCalendarCheck} />}
                                label={t('clubMember.exit.properties.reason')}
                            >
                                <span className="tw-whitespace-pre-line">{clubMember.cancellation_reason}</span>
                            </ContactItem>
                        )}
                    </div>
                </div>
            </div>

            {/* General info row */}
            <div className="tw-flex tw-flex-col tw-gap-1">
                <SectionHeader title={t('clubMember.preview.properties.general')} />
                <Row
                    label={t('clubMember.preview.properties.sportisionAccount')}
                    value={
                        <Badge flat color={clubMember.hasAccount ? 'success' : 'light'} size="xs">
                            {clubMember.hasAccount
                                ? t('clubMember.preview.properties.yes')
                                : t('clubMember.preview.properties.no')}
                        </Badge>
                    }
                />
                {clubMember.debtor_number && (
                    <Row
                        label={t('clubMember.preview.properties.debtorNumber')}
                        value={clubMember.debtor_number}
                    />
                )}
            </div>

            {/* Custom dynamic properties */}
            {properties?.length > 0 && (
                <div className="tw-flex tw-flex-col tw-gap-1">
                    <SectionHeader title={t('clubMember.preview.properties.additionalProperties')} />
                    {properties.map((prop) => (
                        <Row
                            key={prop.label}
                            label={prop.label}
                            value={formatClubMemberPropertyValue(prop.value, prop.type, t) ?? '—'}
                        />
                    ))}
                </div>
            )}

            {/* Interne Notiz */}
            {remarks && (
                <div className="tw-rounded-lg tw-border tw-border-warning/30 tw-bg-warning/10 tw-p-3">
                    <p className="tw-mb-1 tw-text-xs tw-font-semibold tw-text-warning">
                        {t('clubMember.preview.properties.internalNote')}
                    </p>
                    <p className="tw-mb-0 tw-text-sm tw-text-accents-8">{remarks}</p>
                </div>
            )}
        </div>
    );
};

export default ClubMemberPropertiesTab;
